<template>
  <v-container fluid>
    <v-card class="pa-4">
      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-icon color="#ce501b" size="large" class="mr-2">mdi-history</v-icon>
        <h2 class="text-h5">Historial de procesos</h2>
      </div>

      <!-- Filtros -->
      <v-card elevation="2" class="pa-4 mb-4">
        <h3 class="text-subtitle-1 mb-4">Historial</h3>

        <v-row>
          <!-- Selector Actividad -->
          <v-col cols="12" md="3">
            <v-select
              v-model="actividadSeleccionada"
              :items="actividades"
              label="Actividad:"
              variant="outlined"
              density="comfortable"
            ></v-select>
          </v-col>

          <!-- Fecha Inicio -->
          <v-col cols="12" md="3">
            <v-text-field
              v-model="fechaInicio"
              label="Fecha inicio:"
              type="date"
              variant="outlined"
              density="comfortable"
            ></v-text-field>
          </v-col>

          <!-- Fecha Final -->
          <v-col cols="12" md="3">
            <v-text-field
              v-model="fechaFinal"
              label="Fecha final:"
              type="date"
              variant="outlined"
              density="comfortable"
            ></v-text-field>
          </v-col>

          <!-- Botones -->
          <v-col cols="12" md="3" class="d-flex" style="gap: 12px;">
            <v-btn
              color="#ce501b"
              @click="buscarHistorial"
              :loading="buscando"
              elevation="2"
            >
              Buscar
            </v-btn>

            <v-btn
              color="#9B9B9B"
              @click="exportarHistorial"
              :loading="exportando"
              elevation="2"
            >
              Exportar
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Tabla con infinite scroll -->
      <v-card elevation="2">
        <div 
          class="historial-container" 
          @scroll="handleScroll"
          ref="scrollContainer"
        >
          <v-table class="historial-table" fixed-header>
            <thead>
              <tr>
                <th>ID de evento</th>
                <th>Usuario</th>
                <th>Nombre PC</th>
                <th>Nombre Archivo</th>
                <th>Fecha</th>
                <th>Proceso</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(registro, index) in registros"
                :key="index"
                class="registro-row"
              >
                <!-- ID de evento -->
                <td>{{ registro.codigo }}</td>

                <!-- Usuario -->
                <td>{{ registro.usuario }}</td>

                <!-- Nombre PC -->
                <td>{{ registro.nombrePc }}</td>

                <!-- Nombre Archivo -->
                <td>{{ registro.nombreArchivo }}</td>

                <!-- Fecha -->
                <td>{{ formatearFecha(registro.fecha) }}</td>

                <!-- Proceso -->
                <td>{{ registro.proceso }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- Indicador de carga al final -->
          <div v-if="loading" class="text-center py-4">
            <v-progress-circular
              indeterminate
              color="primary"
              size="50"
            ></v-progress-circular>
            <p class="mt-2 text-caption">Cargando más registros...</p>
          </div>

          <!-- Mensaje cuando no hay más datos -->
          <div v-if="!loading && !hayMasDatos && registros.length > 0" class="text-center py-4 text-caption text-grey">
            <v-icon>mdi-check-circle</v-icon>
            No hay más registros para mostrar
          </div>

          <!-- Mensaje cuando no hay datos -->
          <div v-if="!loading && registros.length === 0" class="text-center py-8">
            <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
            <p class="text-h6 text-grey mt-2">No hay registros disponibles</p>
          </div>
        </div>
      </v-card>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/plugins/axios'
import Swal from 'sweetalert2'
import * as XLSX from 'xlsx'  // ✅ Agregar esta línea

// Variables
const registros = ref([])
const todosLosRegistros = ref([])
const loading = ref(false)
const hayMasDatos = ref(true)
const paginaActual = ref(1)
const itemsPorPagina = ref(50)
const scrollContainer = ref(null)
const buscando = ref(false)
const exportando = ref(false)

// Filtros
const actividadSeleccionada = ref('Todos')
const fechaInicio = ref('')
const fechaFinal = ref('')

const actividades = ref(['Todos']) // Iniciar con "Todos"

// Agregar método para cargar actividades
const cargarTiposProceso = async () => {
  try {
    const response = await axios.get('/api/Proceso/list-tipos')
    
    if (response.data.success) {
      actividades.value = response.data.data
      //console.log('Tipos de proceso cargados:', actividades.value.length)  //Usarlo solo para ver los errores en el testeo de Software
    }
  } catch (error) {
    //console.error('Error al cargar tipos de proceso:', error)     //Usarlo solo para ver los errores en el testeo de Software
    // Si falla, dejar "Todos" por defecto
    actividades.value = ['Todos']
  }
}


onMounted(() => {
  // Cargar tipos de proceso dinámicamente
  cargarTiposProceso()
  // Establecer fechas por defecto (último mes)
  const hoy = new Date()
  const haceUnMes = new Date()
  haceUnMes.setMonth(hoy.getMonth() - 1)
  
  fechaFinal.value = hoy.toISOString().split('T')[0]
  fechaInicio.value = haceUnMes.toISOString().split('T')[0]
})

// Buscar historial
const buscarHistorial = async () => {
  // Resetear
  registros.value = []
  todosLosRegistros.value = []
  paginaActual.value = 1
  hayMasDatos.value = true
  
  await cargarHistorial()
}

// Cargar historial con infinite scroll
const cargarHistorial = async () => {
  if (loading.value || !hayMasDatos.value) return
  
  loading.value = true
  buscando.value = true
  
  try {
    if (todosLosRegistros.value.length === 0) {
      const idUsuarioSesion = localStorage.getItem('idUsuario')
      
      if (!idUsuarioSesion) {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: 'No se pudo obtener la información del usuario en sesión',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
        }
        })
        loading.value = false
        buscando.value = false
        return
      }

      // Construir query params
      let url = `/api/Historial/list-historial?idUsuarioSesion=${idUsuarioSesion}`
      
      if (actividadSeleccionada.value && actividadSeleccionada.value !== 'Todos') {
        url += `&actividad=${encodeURIComponent(actividadSeleccionada.value)}`
      }
      
      if (fechaInicio.value) {
        url += `&fechaInicio=${fechaInicio.value}`
      }
      
      if (fechaFinal.value) {
        url += `&fechaFinal=${fechaFinal.value}`
      }

      //console.log('Cargando historial:', url)    //Usarlo solo para ver los errores en el testeo de Software

      const response = await axios.get(url)
      
      if (response.data.success) {
        todosLosRegistros.value = response.data.data
      //console.log('Registros cargados:', todosLosRegistros.value.length)    //Usarlo solo para ver los errores en el testeo de Software
      } else {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: response.data.mensaje || 'No se pudieron cargar los registros',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
          }
        })
        loading.value = false
        buscando.value = false
        return
      }
    }
    
    const inicio = (paginaActual.value - 1) * itemsPorPagina.value
    const fin = inicio + itemsPorPagina.value
    const itemsPaginados = todosLosRegistros.value.slice(inicio, fin)
    
    if (itemsPaginados.length > 0) {
      registros.value = [...registros.value, ...itemsPaginados]
      paginaActual.value++
    }
    
    if (fin >= todosLosRegistros.value.length) {
      hayMasDatos.value = false
    }
    
  } catch (error) {
    //console.error('Error al cargar historial:', error)     //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al cargar el historial',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
       }
    })
  } finally {
    loading.value = false
    buscando.value = false
  }
}

// Handle scroll para infinite scroll
const handleScroll = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target
  if (scrollTop + clientHeight >= scrollHeight * 0.8) {
    cargarHistorial()
  }
}

// Formatear fecha
const formatearFecha = (fecha) => {
  if (!fecha) return ''
  const date = new Date(fecha)
  return date.toLocaleString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

// Exportar historial a Excel
const exportarHistorial = async () => {
  if (todosLosRegistros.value.length === 0) {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ce501b',
      title: 'Sin datos',
      text: 'No hay datos para exportar. Realiza una búsqueda primero.',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
    })
    return
  }

  exportando.value = true
  
  try {
    // Preparar datos para Excel
    const datosParaExcel = todosLosRegistros.value.map(registro => ({
      'Código': registro.codigo,
      'Usuario': registro.usuario,
      'Nombre PC': registro.nombrePc,
      'Nombre Archivo': registro.nombreArchivo || '',
      'Carpeta': registro.carpeta || '',
      'Fecha': formatearFecha(registro.fecha),
      'Proceso': registro.proceso
    }))

    // Crear libro de Excel
    const ws = XLSX.utils.json_to_sheet(datosParaExcel)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Historial')

    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 10 },  // Código
      { wch: 20 },  // Usuario
      { wch: 20 },  // Nombre PC
      { wch: 30 },  // Nombre Archivo
      { wch: 25 },  // Carpeta
      { wch: 25 },  // Fecha
      { wch: 30 }   // Proceso
    ]
    ws['!cols'] = colWidths

    // Generar nombre de archivo con fecha
    const fechaActual = new Date().toISOString().split('T')[0]
    const nombreArchivo = `Historial_Procesos_${fechaActual}.xlsx`

    // Descargar archivo
    XLSX.writeFile(wb, nombreArchivo)

    await Swal.fire({
      icon: 'success',
      iconColor: '#ce501b',
      title: '¡Éxito!',
      text: `Archivo exportado: ${nombreArchivo}`,
      timer: 2500,
      confirmButtonColor: '#ce501b',
      customClass: {
          title: 'swal-title-custom',
          confirmButton: 'swal-btn-confirmar',
          container: 'swal-sobre-modal'
      },
      showConfirmButton: true
    })
  } catch (error) {
    //console.error('Error al exportar:', error)    //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al exportar el historial',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
    })
  } finally {
    exportando.value = false
  }
}

</script>

<style scoped>
.historial-container {
  max-height: calc(100vh - 400px);
  overflow-y: auto;
}

.historial-container::-webkit-scrollbar {
  width: 8px;
}

.historial-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.historial-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.historial-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.historial-table {
  background-color: transparent;
}

.historial-table th {
  background-color: #def2ff !important;
  font-weight: bold !important;
  color: #37495f !important;
  white-space: nowrap;
}

.registro-row {
  transition: background-color 0.2s ease;
}

.registro-row:hover {
  background-color: rgba(59, 130, 200, 0.05);
}

.historial-table td {
  padding: 12px 16px !important;
  font-size: 0.875rem;
}
</style>


<style>

.swal-title-custom {
  color: #004d82 !important;
  font-size: 1.3rem !important;
}

.swal-text-custom {
  color: #555555 !important;
  font-size: 0.95rem !important;
}

.swal-btn-custom {
  background-color: #ce501b !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  padding: 8px 24px !important;
}

.swal-btn-custom:hover {
  background-color: #a83d14 !important;
}

.swal-btn-confirmar {
  color: white !important;
}

</style>

<style>
.swal-sobre-modal {
  z-index: 10000 !important;
}
</style>
